import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./AssessMenu.css";

const MENU_WIDTH = 268;
const GAP = 6;

/**
 * Small popover menu anchored to a trigger element.
 *
 * Rendered in a portal with `position: fixed` rather than inside the cell:
 * the products table lives in a `.table-responsive` (overflow-x: auto), which
 * would clip an absolutely-positioned menu. Fixed positioning also means the
 * menu can escape the last row without the table growing a scrollbar.
 *
 * Items: { key, label, hint, badge, onSelect, disabled, tone }
 *   tone 'primary' tints the row — used for the action we want picked first.
 * Deliberately icon-free: a mixed column of glyphs and emoji is the first thing
 * that makes a menu like this look untidy, and the labels are short enough.
 */
export default function AssessMenu({ anchorEl, items = [], onClose }) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const height = menuRef.current?.offsetHeight || 0;

    // Prefer below-left of the trigger; flip when the viewport runs out.
    let left = rect.left;
    if (left + MENU_WIDTH > window.innerWidth - 8) {
      left = Math.max(8, rect.right - MENU_WIDTH);
    }
    let top = rect.bottom + GAP;
    if (height && top + height > window.innerHeight - 8) {
      const above = rect.top - GAP - height;
      if (above > 8) top = above;
      else top = Math.max(8, window.innerHeight - 8 - height);
    }
    setPos({ top, left });
  }, [anchorEl]);

  useLayoutEffect(() => {
    place();
  }, [place, items.length]);

  useEffect(() => {
    if (!anchorEl) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    // Any scroll or resize invalidates the anchor position — close rather than
    // chase it, which is what a menu like this should do anyway.
    const onScrollOrResize = () => onClose();
    const onPointerDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (anchorEl.contains(e.target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [anchorEl, onClose]);

  if (!anchorEl) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="assess-menu"
      role="menu"
      style={{
        width: MENU_WIDTH,
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        visibility: pos ? "visible" : "hidden",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="menuitem"
          className={`assess-menu__item${item.tone === "primary" ? " assess-menu__item--primary" : ""}`}
          disabled={item.disabled}
          onClick={() => {
            if (item.disabled) return;
            onClose();
            item.onSelect?.();
          }}
        >
          <span className="assess-menu__body">
            <span className="assess-menu__label">
              {item.label}
              {item.badge && <span className="assess-menu__badge">{item.badge}</span>}
            </span>
            {item.hint && <span className="assess-menu__hint">{item.hint}</span>}
          </span>
        </button>
      ))}
    </div>,
    document.body,
  );
}
